import { useSelector } from "react-redux";
import { useRelativeTime } from "./useRelativeTime";
import { getSignalTypePercentages } from "../utility/helper";
import {
  LIVE_TIMEFRAMES,
  liveSuggestionFor,
} from "../redux/live-suggestion/liveSuggestionSlice";

const renderSignalColor = (signalType) => {
  const signal = signalType?.toLowerCase();
  return signal?.includes("sell")
    ? "#C0392B"
    : signal?.includes("buy")
      ? "#27AE60"
      : signal?.includes("hold")
        ? "#1B6BD3"
        : "#0f1c53";
};

// opposite signal can be ai, insight and dashboard, ai-trade, insight-trade
function UseSuggestedSignal({ coin, oppositeSignal = "", timeframe }) {
  const {
    tradingAssistant: {
      tradeAssistant: {
        ASSISTANT_SUGGESTIONS: assistantSuggestion = [],
        coefficients,
        signal_timestamp,
      } = {},
      allTradeSuggestionData,
      loading,
      allTradeSuggestionLoading,
    },
    otherCoinsAnalyze: { analyze, status },
    liveSuggestion: { suggestions, coinStatus, liveTimeframe },
    tradeForm: { aiSuggestion: tradeFormAiSuggestion },
    newInsightState: { signalData, signalTableColumns },
  } = useSelector((state) => state);

  const liveFrame = LIVE_TIMEFRAMES.includes(timeframe)
    ? timeframe
    : liveTimeframe;
  const pinLiveFrame = LIVE_TIMEFRAMES.includes(timeframe);

  // ------------------------1H----------------------------------
  const oneHourSuggestions = [
    ...assistantSuggestion,
    ...allTradeSuggestionData,
    ...analyze,
  ];

  const aiSuggestion = oneHourSuggestions.find((s) => s?.SYMBOL_NAME === coin);

  const aiSuggestionLoading =
    status === "loading" || loading || allTradeSuggestionLoading;

  const prediction1H = aiSuggestion?.TRADE_TYPE?.split("/")?.[0];
  const aiStrength = aiSuggestion?.SIGNAL_STRENGTH * 20;
  const aiSignalPrefix = prediction1H?.includes("Hold")
    ? ""
    : aiStrength >= 50
      ? "Strong"
      : "Weak";

  const aiPrediction = aiSignalPrefix + " " + prediction1H;
  const aiSL = aiSuggestion?.STOP_LOSS_PERCENT * coefficients?.stop_loss;
  const aiTP = aiSuggestion?.TAKE_PROFIT_PERCENT * coefficients?.take_profit;
  const aiPositionSize = aiSuggestion?.SUGGESTED_AMOUNT_PERCENTAGE * 100;
  const aiDate = useRelativeTime(signal_timestamp);
  const aiSignalColor = renderSignalColor(prediction1H);

  const aiData = {
    suggestion: aiSuggestion,
    loading: aiSuggestionLoading,
    tradeType: prediction1H,
    strength: aiStrength,
    signal: aiPrediction?.includes("undefined") ? "" : aiPrediction,
    sl: aiSL,
    tp: aiTP,
    date: aiDate,
    signalColor: aiSignalColor,
    positionSize: aiPositionSize,
  };

  // ------------------------1H----------------------------------

  // ------------------------INSIGHTS----------------------------
  const liveSignal = liveSuggestionFor(suggestions, coin, liveFrame);

  const percentages = getSignalTypePercentages(
    signalData.filter((s) => s.coin === coin),
    signalTableColumns,
    liveSignal,
  );

  const { buy, sell, hold } = percentages;

  const insightSuggestion =
    buy > sell && buy >= hold
      ? "Buy"
      : sell >= hold
        ? "Sell"
        : hold
          ? "Hold"
          : "";
  // ------------------------INSIGHTS----------------------------

  // ------------------------15m---------------------------------

  const liveSuggestion =
    (oppositeSignal === "ai-trade" ||
      oppositeSignal === "trade" ||
      oppositeSignal === "insight-trade") &&
    tradeFormAiSuggestion?.prediction
      ? tradeFormAiSuggestion
      : liveSuggestionFor(suggestions, coin, liveFrame);

  const liveSuggestionLoading = coinStatus?.[coin] === "loading";

  const prediction15m =
    oppositeSignal === "insight-trade" && insightSuggestion
      ? insightSuggestion
      : liveSuggestion?.prediction || "Hold";

  const liveStrength = (liveSuggestion?.confidence || 50) * 100;

  const liveSignalPrefix =
    prediction15m === "Hold" ? "" : liveStrength >= 75 ? "Strong" : "Weak";

  const livePrediction = liveSignalPrefix + " " + prediction15m;

  const liveParams = liveSuggestion?.position_params;

  const liveSL = liveParams?.sl_percent?.split("%")?.[0];
  const liveTP = liveParams?.tp_percent?.split("%")?.[0];
  const livePositionSize = liveParams?.position_size;

  const liveDate = useRelativeTime(liveSuggestion?.signal_timestamp);

  const liveSignalColor = renderSignalColor(prediction15m);

  const liveData = {
    suggestion: liveSuggestion,
    loading: liveSuggestionLoading,
    tradeType: prediction15m || "-",
    strength: liveStrength || "-",
    signal: livePrediction || "-",
    sl: liveSL || "-",
    tp: liveTP || "-",
    date: liveDate || "",
    signalColor: liveSignalColor,
  };
  // ------------------------15m---------------------------------

  const allSignals = {
    ai: aiData,
    "ai-trade": aiData,
  };

  const comparedSignal = allSignals?.[oppositeSignal];

  const isLoading = comparedSignal?.loading || liveSuggestionLoading;

  const finalSuggestion =
    oppositeSignal === "" ||
    !comparedSignal ||
    (liveSuggestion && prediction15m !== "Hold")
      ? liveSuggestion
      : comparedSignal?.suggestion || liveSuggestion || "";

  const suggestedSignal =
    oppositeSignal === "" ||
    !comparedSignal ||
    (liveSuggestion && liveSuggestion?.prediction !== "Hold")
      ? "15m"
      : "1h";

  const signalStrength =
    suggestedSignal === "15m" ? liveStrength : comparedSignal?.strength;

  const signal = pinLiveFrame
    ? liveSuggestion?.prediction
      ? livePrediction
      : ""
    : suggestedSignal === "1h" && comparedSignal?.signal
      ? comparedSignal?.signal
      : livePrediction;

  const stopLoss =
    pinLiveFrame || liveSuggestion ? liveSL : comparedSignal?.sl;

  const takeProfit =
    pinLiveFrame || liveSuggestion ? liveTP : comparedSignal?.tp;

  const positionSize =
    pinLiveFrame || liveSuggestion
      ? livePositionSize
      : comparedSignal?.positionSize;

  const chartData = liveSuggestion?.backtest_result?.equity;

  const historyData = liveSuggestion?.backtest_result?.trades || [];

  const signalColor = renderSignalColor(signal);

  const signalDate =
    !pinLiveFrame && suggestedSignal === "1h" ? comparedSignal?.date : liveDate;

  return {
    signal,
    finalSuggestion,
    stopLoss,
    takeProfit,
    positionSize,
    isLoading,
    liveSuggestion,
    historyData,
    chartData,
    liveSuggestionLoading,
    signalColor,
    aiSuggestion,
    suggestedSignal,
    signalDate,
    signalStrength,
    livePrediction,
    liveSignalColor,
    liveData,
    aiData,
    liveParams,
  };
}

export default UseSuggestedSignal;
